// Design tokens. Single source of truth for colour, type, space, motion.
// Change these here; do not duplicate values in component files.
//
// Naming convention:
//   color.<role>.<weight>   e.g. color.brand[500]   — raw palette
//   color.semantic.<intent> e.g. color.semantic.text  — meaning-based
// Components MUST reach for semantic colours where possible so dark-mode
// (or rebrand) is a one-line change in this file.

export const tokens = {
  color: {
    // ── Raw scales ────────────────────────────────────────────────────
    neutral: {
      0:   "#ffffff",
      50:  "#f8fafc",
      100: "#f1f5f9",
      200: "#e2e8f0",
      300: "#cbd5e1",
      400: "#94a3b8",
      500: "#64748b",
      600: "#475569",
      700: "#334155",
      800: "#1e293b",
      900: "#0f172a",
    },
    // Crimson — Nepali flag inspired. Audited at WCAG AA on neutral.0
    // (white) for body text and on neutral.50 for buttons.
    brand: {
      50:  "#fef2f2",
      500: "#dc2626",
      600: "#b91c1c",
      700: "#991b1b",
    },
    success: { 50: "#f0fdf4", 500: "#16a34a", 700: "#15803d" },
    warning: { 50: "#fefce8", 500: "#eab308", 700: "#a16207" },
    danger:  { 50: "#fef2f2", 500: "#dc2626", 700: "#991b1b" },
    info:    { 50: "#eff6ff", 500: "#2563eb", 700: "#1d4ed8" },

    // ── Semantic aliases ──────────────────────────────────────────────
    // Names that describe purpose, not appearance. Components consume
    // these so a re-skin / dark-mode flip only touches THIS file.
    semantic: {
      bg:           "#ffffff",   // page background
      bgSubtle:     "#f8fafc",   // sidebar / card backgrounds
      backdrop:     "#fef7ed",   // page-fill gradient stop 1 (warm sand)
      backdropAlt:  "#ffffff",   // page-fill gradient stop 2
      surface:      "#ffffff",   // raised cards
      surfaceAlt:   "#f1f5f9",   // alt-shaded cards
      border:       "#e2e8f0",   // 1px hairlines
      borderStrong: "#cbd5e1",   // form fields, dividers
      text:         "#0f172a",   // body text (AAA on bg)
      heading:      "#0c1c3a",   // deep navy headings (easier on eyes than pure black)
      textMuted:    "#475569",   // helper text, captions
      textInverse:  "#ffffff",   // text on brand bg
      link:         "#1d4ed8",
      focusRing:    "#2563eb",   // :focus-visible outline
    },
  },

  // ── Typography ──────────────────────────────────────────────────────
  // Devanagari needs ~1.6 line-height vs ~1.4 for Latin or it crashes
  // into the next line. `:lang(ne)` rule in global.css uses these.
  type: {
    family: {
      sans:  '"Inter","Noto Sans Devanagari","Segoe UI",system-ui,-apple-system,sans-serif',
      ne:    '"Noto Sans Devanagari","Inter",system-ui,sans-serif',
      mono:  '"Roboto Mono","Courier New",monospace',
    },
    size: {
      xs:   "12px",
      sm:   "14px",
      base: "16px",
      lg:   "18px",
      xl:   "20px",
      "2xl":"24px",
      "3xl":"32px",
    },
    weight: {
      regular:  400,
      medium:   500,
      semibold: 600,
      bold:     700,
    },
    leading: {
      tight:  1.2,
      normal: 1.4,
      relaxed: 1.6, // for `:lang(ne)` body copy
    },
  },

  // ── Spacing ─────────────────────────────────────────────────────────
  // 4px-grid scale; index doubles roughly every 2 steps.
  space: {
    0:   "0",
    1:   "4px",
    2:   "8px",
    3:   "12px",
    4:   "16px",
    5:   "20px",
    6:   "24px",
    7:   "28px",   // card-to-card vertical rhythm on auth screens
    8:   "32px",
    10:  "40px",
    12:  "48px",
    16:  "64px",
    20:  "80px",
  },

  // ── Sizing ──────────────────────────────────────────────────────────
  size: {
    // Minimum tap targets (NFR-A11Y-02). 48dp default; 56dp for the
    // gate kiosk where the user is wearing gloves or moving fast.
    tap48: "48px",
    tap56: "56px",
    // Layout caps
    maxContent: "720px",
    maxNarrow:  "560px",
  },

  // ── Surfaces ────────────────────────────────────────────────────────
  radius: {
    none: "0",
    sm:   "4px",
    md:   "8px",
    lg:   "12px",
    xl:   "16px",
    pill: "999px",
  },
  shadow: {
    sm: "0 1px 2px rgba(15, 23, 42, 0.06)",
    md: "0 2px 6px rgba(15, 23, 42, 0.08), 0 1px 2px rgba(15, 23, 42, 0.04)",
    lg: "0 8px 24px rgba(15, 23, 42, 0.12)",
    // Focus halo, layered on top of native outline.
    focus: "0 0 0 3px rgba(37, 99, 235, 0.35)",
  },

  // ── Motion ──────────────────────────────────────────────────────────
  // Honours prefers-reduced-motion via global.css.
  motion: {
    durFast:   "120ms",
    durBase:   "180ms",
    durSlow:   "320ms",
    easeOut:   "cubic-bezier(0.16, 1, 0.3, 1)",
    easeInOut: "cubic-bezier(0.65, 0, 0.35, 1)",
  },

  // ── Z-index ────────────────────────────────────────────────────────
  z: {
    base:    0,
    sticky:  10,
    overlay: 100,
    modal:   1000,
    toast:   1100,
  },

  // ── Breakpoints (mobile-first) ─────────────────────────────────────
  // Use as `@media (min-width: ${tokens.bp.md})`.
  bp: {
    sm:  "480px",  // phone landscape
    md:  "768px",  // tablet
    lg:  "1024px", // small laptop
    xl:  "1280px", // desktop
  },
} as const;

export type Tokens = typeof tokens;
